import React, { useCallback, useEffect, useState } from 'react';
import ExternalLink from 'lucide-react/dist/esm/icons/external-link';
import { PatternFormat } from 'react-number-format';
import { Text, Badge } from '../../../../components/ui';
import { useUIStore } from '../../../../stores/uiStore';
import { getTranslation } from '../../../../i18n';
import { formatDate } from '../shared';
import { amlApi } from '../../services/amlApi';
import type { AmlCustomer, ScreeningMatch } from '../../types/aml';

interface CustomerDetailViewProps {
  customer: AmlCustomer;
}

const OFAC_DETAILS_URL = 'https://sanctionssearch.ofac.treas.gov/Details.aspx?id=';

const SCREENING_MAP = {
  cleared: { variant: 'success', labelKey: 'amlScreeningResultCleared' },
  potential_match: { variant: 'danger', labelKey: 'amlScreeningResultPotentialMatch' },
} as const;

function formatScore(value: string | null): string {
  if (value == null) return '—';
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? value : parsed.toFixed(1);
}

function formatCheckedAt(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

const FieldRow: React.FC<{ label: string; value: string | number | null | undefined }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col gap-1">
    <Text variant="label">{label}</Text>
    <span className="text-sm text-content">{value ?? '—'}</span>
  </div>
);

const CustomerDetailView: React.FC<CustomerDetailViewProps> = ({ customer }) => {
  const { language } = useUIStore();
  const t = useCallback((key: string) => getTranslation(language, key), [language]);

  const [screeningMatches, setScreeningMatches] = useState<ScreeningMatch[]>([]);

  const screeningMeta = customer.screening_status
    ? SCREENING_MAP[customer.screening_status]
    : null;

  // Only a flagged customer has candidate hits worth fetching.
  useEffect(() => {
    if (customer.screening_status !== 'potential_match') {
      setScreeningMatches([]);
      return;
    }
    let cancelled = false;
    amlApi.getCustomerScreening(customer.id)
      .then((data) => {
        if (!cancelled) setScreeningMatches(data.result?.matches ?? []);
      })
      .catch(() => {
        if (!cancelled) setScreeningMatches([]);
      });
    return () => { cancelled = true; };
  }, [customer.id, customer.screening_status]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <FieldRow label={t('amlCustomerFieldCustomerLegalName')} value={customer.name} />
        <FieldRow label={t('amlCustomerFieldCode')} value={customer.code} />
        <FieldRow label={t('amlCustomerFieldRegistrationNumber')} value={customer.registration_number} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <FieldRow label={t('amlCustomerFieldEinFein')} value={customer.ein_or_fein} />
        <FieldRow label={t('amlCustomerFieldInitialDate')} value={formatDate(customer.initial_date)} />
        <FieldRow label={t('amlCustomerFieldDateCompanyEstablished')} value={formatDate(customer.established_date)} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <FieldRow label={t('amlCustomerFieldCompanyLegalRepresentative')} value={customer.legal_representative} />
        <FieldRow label={t('amlCustomerFieldMainAddress')} value={customer.address} />
        <FieldRow label={t('amlCustomerFieldServicesAndProduct')} value={customer.service_and_product} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FieldRow label={t('amlCustomerFieldTypeOfBusinessActivity')} value={customer.activity_type} />
        <FieldRow label={t('amlCustomerFieldNotesAmlRev')} value={customer.notes} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <FieldRow label={t('amlCustomerFieldContact')} value={customer.contact} />
        <div className="flex flex-col gap-1">
          <Text variant="label">{t('amlCustomerFieldEmail')}</Text>
          {customer.email ? (
            <a
              href={`mailto:${customer.email}`}
              className="text-sm text-status-info hover:underline break-all"
            >
              {customer.email}
            </a>
          ) : (
            <span className="text-sm text-content">—</span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <Text variant="label">{t('amlCustomerFieldPhone')}</Text>
          <span className="text-sm text-content">
            {customer.phone ? (
              <PatternFormat
                value={customer.phone}
                format="+### (###) ###-####"
                displayType="text"
                renderText={(v) => <>{v}</>}
              />
            ) : '—'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <Text variant="label" className="block mb-1">{t('amlCustomerFieldStatus')}</Text>
          <Badge variant={customer.status ? 'success' : 'neutral'} size="sm">
            {customer.status ? t('amlCustomerStatusActive') : t('amlCustomerStatusInactive')}
          </Badge>
        </div>

        <div>
          <Text variant="label" className="block mb-1">{t('amlColScreening')}</Text>
          {screeningMeta ? (
            <>
              <Badge variant={screeningMeta.variant} size="sm" dot>
                {t(screeningMeta.labelKey)}
              </Badge>
              <Text variant="caption" className="mt-1.5 block">
                {t('amlScreeningBestScore')}: {formatScore(customer.screening_score)}%
                {' · '}
                {formatCheckedAt(customer.screening_checked_at)}
              </Text>
            </>
          ) : (
            <Badge variant="neutral" size="sm">
              {t('amlScreeningResultNotScreened')}
            </Badge>
          )}
        </div>
      </div>

      {screeningMatches.length > 0 && (
        <div>
          <Text variant="subheading" className="block mb-2">
            {t('amlScreeningCandidatesTitle')}
          </Text>
          <div className="space-y-2">
            {screeningMatches.map((match) => (
              <div
                key={match.id}
                className="flex items-start justify-between gap-3 py-2 px-3 rounded-lg bg-status-error/5 border border-subtle"
              >
                <div className="min-w-0">
                  <span className="text-sm text-content-primary break-words">
                    {match.matched_name}
                  </span>
                  {match.name_type === 'aka' && match.primary_name ? (
                    <Text variant="caption" className="block">
                      {t('amlScreeningAkaOf')}: {match.primary_name}
                    </Text>
                  ) : null}
                  {match.programs?.length ? (
                    <Text variant="caption" className="block">
                      {match.programs.join(', ')}
                    </Text>
                  ) : null}
                  {match.ofac_uid != null ? (
                    <a
                      href={`${OFAC_DETAILS_URL}${match.ofac_uid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('amlScreeningViewOnOfac')}
                      <ExternalLink size={14} aria-hidden="true" />
                    </a>
                  ) : null}
                </div>
                <Badge variant="danger" size="sm">
                  {formatScore(match.score)}%
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {customer.locations && customer.locations.length > 0 && (
        <div>
          <Text variant="subheading" className="block mb-3">{t('amlCustomerDetailLocationsDetail')}</Text>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {customer.locations.map((loc) => (
              <div
                key={loc.id}
                className="text-sm text-content-primary py-1.5 px-3 rounded-lg bg-surface-secondary/50"
              >
                {loc.name}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetailView;
